import React, { useState } from 'react';
import { NavLink as RouterLink, matchPath, useLocation } from 'react-router-dom';
import { alpha, useTheme, styled } from '@mui/material/styles';
import { Box, List, Collapse, ListItemText, ListItemIcon, ListItemButton } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { PRIMARY_COLOR, SECONDARY_COLOR } from "../constants/Colors";
import { AuthenticationContext } from "../contexts/AuthContext";

const ListItemStyle = styled((props) => <ListItemButton disableGutters {...props} />)(
    ({ theme }) => ({
        ...theme.typography.body2,
        height: 48,
        position: 'relative',
        textTransform: 'capitalize',
        paddingLeft: theme.spacing(5),
        paddingRight: theme.spacing(2.5),
        color: SECONDARY_COLOR,
        '&:before': {
            top: 0,
            right: 0,
            width: 3,
            bottom: 0,
            content: "''",
            display: 'none',
            position: 'absolute',
            borderTopLeftRadius: 4,
            borderBottomLeftRadius: 4,
            backgroundColor: PRIMARY_COLOR
        }
    })
);

const ListItemIconStyle = styled(ListItemIcon)({
    width: 22,
    height: 22,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
});

function NavItem({ item, active }) {
    const theme = useTheme();
    const isActiveRoot = active(item.path);
    const { title, path, icon, info, children } = item;
    const [open, setOpen] = useState(isActiveRoot);

    const getCount = () => '';

    const handleOpen = () => {
        setOpen((prev) => !prev);
    };

    const activeRootStyle = {
        color: PRIMARY_COLOR,
        fontWeight: 'fontWeightMedium',
        bgcolor: alpha(PRIMARY_COLOR, theme.palette.action.selectedOpacity),
        '&:before': { display: 'block' }
    };

    const activeSubStyle = {
        color: PRIMARY_COLOR,
        fontWeight: 'fontWeightMedium'
    };

    // 🔥 helper to force icon color
    const renderIcon = (icon, isActive) => {
        if (!icon) return null;
        return React.cloneElement(icon, {
            sx: {
                color: isActive ? PRIMARY_COLOR : SECONDARY_COLOR
            }
        });
    };

    if (children) {
        return (
            <>
                <ListItemStyle
                    onClick={handleOpen}
                    sx={{
                        ...(isActiveRoot && activeRootStyle)
                    }}
                >
                    <ListItemIconStyle>
                        {renderIcon(icon, isActiveRoot)}
                    </ListItemIconStyle>

                    <ListItemText disableTypography primary={title} />

                    {info && info}

                    {open ? <ArrowDownwardIcon /> : <ArrowForwardIosIcon />}
                </ListItemStyle>

                <Collapse in={open} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {children.map((item) => {
                            const { title, path } = item;
                            const isActiveSub = active(path);

                            return (
                                <ListItemStyle
                                    key={title}
                                    component={RouterLink}
                                    to={path}
                                    sx={{
                                        ...(isActiveSub && activeSubStyle)
                                    }}
                                >
                                    <ListItemIconStyle>
                                        <Box
                                            component="span"
                                            sx={{
                                                width: 4,
                                                height: 4,
                                                display: 'flex',
                                                borderRadius: '50%',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor: isActiveSub ? PRIMARY_COLOR : 'text.disabled',
                                                transform: isActiveSub ? 'scale(2)' : 'scale(1)',
                                                transition: (theme) =>
                                                    theme.transitions.create('transform')
                                            }}
                                        />
                                    </ListItemIconStyle>

                                    <ListItemText
                                        style={{
                                            color: getCount(title) === '' ? '' : PRIMARY_COLOR
                                        }}
                                        disableTypography
                                        primary={title + " " + getCount(title)}
                                    />
                                </ListItemStyle>
                            );
                        })}
                    </List>
                </Collapse>
            </>
        );
    }

    return (
        <ListItemStyle
            component={RouterLink}
            to={path}
            sx={{
                ...(isActiveRoot && activeRootStyle)
            }}
        >
            <ListItemIconStyle>
                {renderIcon(icon, isActiveRoot)}
            </ListItemIconStyle>

            <ListItemText
                style={{
                    color: getCount(title) === '' ? '' : PRIMARY_COLOR
                }}
                disableTypography
                primary={title + " " + getCount(title)}
            />

            {info && info}
        </ListItemStyle>
    );
}

export default function NavSection({ navConfig, ...other }) {
    const { pathname } = useLocation();
    const match = (path) =>
        path ? !!matchPath({ path, end: false }, pathname) : false;

    const authenticationContext = React.useContext(AuthenticationContext);
    const { userAccount } = authenticationContext;

    return (
        <Box {...other}>
            <List disablePadding>
                {navConfig.map((item) =>
                    item.title === 'Brochure Requests' &&
                    !(userAccount?.permissions?.includes('VIEW_BROCHURE_REQUESTS')) ? null : (
                        <NavItem key={item.title} item={item} active={match} />
                    )
                )}
            </List>
        </Box>
    );
}